import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { securitiesAccountsSelector } from '!core/redux/userSecuritiesAccounts/userSecuritiesAccounts.selectors';
import { TrlFormDropdown } from '»components/atoms/Inputs';
import { TrlInfoField } from '»components/atoms/Inputs/TrlInfoField';
import { useCurrentPage } from '»utils/customHooks';
import { getFundType } from '»utils/funds';
import { TrlNumberFormatted } from '»utils/number';

import { TrlDisclaimer } from '../TrlDisclaimer';
import { TrlFundsFields } from './TrlFundsFields';
import { TrlStockExchangeFields } from './TrlStockExchangeFields';
import {
  BalanceStyled,
  ContainerBottomStyled,
  ContainerTopStyled,
  CurrencyStyled,
  FormFieldsWrapperStyled,
} from './TrlStockExchangeMarketInputData.styles';

export const TrlStockExchangeMarketInputData = memo(
  ({
    quantityAvailable,
    orderPossibilities,
    instrumentInfo,
    balanceAccounts,
  }) => {
    const securityAccounts = useSelector(securitiesAccountsSelector);

    const { values, setFieldValue } = useFormikContext();

    const { isMarketStockExchangeOrders } = useCurrentPage();

    const isFunds = !!getFundType(instrumentInfo);

    const { symbol, exchange, securityType, subscriptionType } = instrumentInfo;

    const securityAccountsRefactored = securityAccounts.reduce(
      (acc, account) => {
        acc.push({ value: account.value, label: account.label });

        return acc;
      },
      [],
    );

    const {
      selectSecurityAccount,
      selectBalanceAccount,
      selectOperation,
      selectOrderType,
    } = values;

    const currentBalanceAccList = balanceAccounts[selectSecurityAccount];

    const currentBalanceAccListRefac =
      currentBalanceAccList?.reduce((acc, account) => {
        acc.push({ value: account.value, label: account.label });

        return acc;
      }, []) ?? [];

    const selectedBalanceAccData = currentBalanceAccList?.find(
      (account) => account.value === selectBalanceAccount,
    );

    const onChangeSecurityAccount = (e) => {
      const currentBalanceAccounts = balanceAccounts[e.target.value];

      if (currentBalanceAccounts.length > 0) {
        const newSelectedBalanceAccount = currentBalanceAccounts[0].value;
        setFieldValue('selectBalanceAccount', newSelectedBalanceAccount, false);
      } else {
        setFieldValue('selectBalanceAccount', '', false);
      }
    };

    const getFundsExtraField = () => {
      if (!quantityAvailable) {
        if (selectOrderType === 3) {
          const minimumSub =
            instrumentInfo.initialAmount / instrumentInfo.lastPx;
          return minimumSub === Infinity ? '-' : minimumSub;
        }

        if (instrumentInfo.initialAmount !== null)
          return `${instrumentInfo.initialAmount} ${instrumentInfo.currencySymbol}`;

        return '-';
      }

      if (selectOrderType === 3) {
        const minimumSub =
          instrumentInfo.additionalAmount / instrumentInfo.lastPx;
        return minimumSub === Infinity ? '-' : minimumSub;
      }

      if (instrumentInfo.additionalAmount !== null)
        return `${instrumentInfo.additionalAmount} ${instrumentInfo.currencySymbol}`;

      return '-';
    };

    return (
      <FormFieldsWrapperStyled>
        <ContainerTopStyled>
          <TrlFormDropdown
            name="selectSecurityAccount"
            label={<FormattedMessage id="TRLT_UI-CUSTODIAN_ACCOUNT" />}
            value={selectSecurityAccount}
            options={securityAccountsRefactored}
            noPaddingTop
            onChangeValue={onChangeSecurityAccount}
            disabled={isMarketStockExchangeOrders}
          />
          <TrlFormDropdown
            name="selectBalanceAccount"
            label={<FormattedMessage id="TRLT_UI-CASH_ACCOUNT" />}
            value={selectBalanceAccount}
            options={currentBalanceAccListRefac}
            noPaddingTop
            disabled={isMarketStockExchangeOrders}
          />
          <TrlInfoField
            value={
              selectedBalanceAccData ? (
                <BalanceStyled>
                  <TrlNumberFormatted
                    value={selectedBalanceAccData.balance}
                    fontFamily="Roboto"
                  />
                  <CurrencyStyled>
                    {selectedBalanceAccData.currency}
                  </CurrencyStyled>
                </BalanceStyled>
              ) : (
                '-'
              )
            }
            label={<FormattedMessage id="TRLT_UI-TRADING_BALANCE" />}
          />
          <TrlInfoField
            value={quantityAvailable}
            label={<FormattedMessage id="TRLT_UI-AVAILABLE_QTY" />}
          />
          {isFunds && (
            <TrlInfoField
              value={getFundsExtraField()}
              label={<FormattedMessage id="TRLT_UI-MINIMUM_SUB" />}
            />
          )}
        </ContainerTopStyled>
        <ContainerBottomStyled id="bottomFormContainer">
          {isFunds ? (
            <TrlFundsFields subscriptionType={subscriptionType} />
          ) : (
            <TrlStockExchangeFields
              orderPossibilities={orderPossibilities}
              instrumentInfo={instrumentInfo}
            />
          )}
        </ContainerBottomStyled>
        <TrlDisclaimer
          disclaimerId={isFunds ? 'NEG_FUND_BOLSA' : 'NEG_BOLSA'}
          operationType={selectOperation === 1 ? 'B' : 'S'}
          symbol={symbol}
          exchange={exchange}
          securityType={securityType}
          isModal
        />
      </FormFieldsWrapperStyled>
    );
  },
);

TrlStockExchangeMarketInputData.displayName = 'TrlStockExchangeMarketInputData';

TrlStockExchangeMarketInputData.propTypes = {
  quantityAvailable: PropTypes.number.isRequired,
  orderPossibilities: PropTypes.array.isRequired,
  instrumentInfo: PropTypes.object.isRequired,
  balanceAccounts: PropTypes.array.isRequired,
};
